import ReportCard from '@Components/ReportCard/ReportCard';
import DashboardService from '@Services/Dashboard.services.ts';
import { AssetInfoStatus } from '@Types/Asset.types';
import { ScannerCard } from '@Types/Scanner.types';
import { ReaderInfoResponse, ReaderStatusWeekly } from '@Types/Scanner.types.ts';
import { PATHS } from '@Utils/constants';
import { getAssetStatusIcon, getCheckCards, getModifierAsset, getWeeklyFails } from '@Utils/reader.utils.tsx';
import { ErrorDisplay, useTopHat, useUser } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import styles from './Home.module.scss';

const Home = () => {
  const user = useUser();
  const [loading, setLoading] = useState<boolean>(true);
  const [dataObtainFailed, setDataObtainFailed] = useState<boolean>(false);
  const [readerStatus, setReaderStatus] = useState<ReaderInfoResponse>();
  const [weeklyFails, setWeeklyFails] = useState<ReaderStatusWeekly>();
  const [assetsInfo, setAssetsInfo] = useState<AssetInfoStatus[]>([]);
  const [checksCards, setChecksCards] = useState<ScannerCard[]>([]);

  const DEFAULT_PERCENTAGE = 'N/A';
  const DEFAULT_AMOUNT = '0';
  const block = 'home';

  useTopHat({
    activeNav: 'nav-home',
    activeTab: undefined,
    tabs: [],
    breadcrumbs: [
      {
        title: 'Home',
        href: `${PATHS.HOME}`,
        id: 'Home',
      },
    ],
  });

  const getStatus = async () => {
    Promise.all([
      DashboardService.getReaderServiceStatus(),
      DashboardService.getWeeklyComparisons(),
      DashboardService.getAssetsByAssetStatus(),
      DashboardService.getWeeklyReaderServiceStatusCount(),
    ])
      .then(([readerServiceStatus, weeklyComparisons, amountAssetStatus, weeklyFailsResponse]) => {
        setWeeklyFails(getWeeklyFails(weeklyFailsResponse, 'Failed', 'Terra'));
        setReaderStatus(readerServiceStatus.data[0]);
        setAssetsInfo(amountAssetStatus.data);
        setChecksCards(getCheckCards(weeklyComparisons, DEFAULT_PERCENTAGE, DEFAULT_AMOUNT));
      })
      .catch(() => {
        setDataObtainFailed(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setLoading(true);
    setDataObtainFailed(false);
    getStatus();
  }, []);

  if (dataObtainFailed) {
    return <ErrorDisplay inline title='Error loading dashboard' />;
  }

  return (
    <>
      <p>
        <b>
          {'Welcome'} {user && user.details?.nameFirst}!
        </b>
      </p>
      <br />
      <h2>Scanner Info</h2>
      <div className={styles[`${block}__cards-container`]}>
        <ReportCard
          classname='col-3'
          description={`Office: ${readerStatus?.officeName}`}
          icon={<i className='fa-light fa-ballot-check' />}
          status={readerStatus?.status || ''}
          title='Reader Status'
          statusModifier={readerStatus?.status === 'Running' ? 'green' : ''}
          loading={loading}
        />
        {/* Unable to use map, card has to be created in order to show the Loader  */}
        <ReportCard
          classname='col-3'
          description={checksCards[0]?.description}
          icon={<i className={`fa-solid ${checksCards[0]?.icon}`} />}
          percentage={checksCards[0]?.percentage}
          status={checksCards[0]?.amount || ''}
          title={checksCards[0]?.title}
          loading={loading}
        />
        <ReportCard
          classname='col-3'
          description={checksCards[1]?.description}
          icon={<i className={`fa-solid ${checksCards[1]?.icon}`} />}
          percentage={checksCards[1]?.percentage}
          status={checksCards[1]?.amount || ''}
          title={checksCards[1]?.title}
          loading={loading}
        />
        <ReportCard
          classname='col-3'
          description={`Office: ${weeklyFails?.officeName}`}
          icon={<i className='fa-regular fa-file-exclamation' />}
          status={weeklyFails?.amount?.toString() || ''}
          title='Reader Weekly'
          loading={loading}
        />
      </div>
      <br />
      <h2>Asset Info</h2>
      <div className={styles[`${block}__cards-container`]}>
        {/* Unable to use map, card has to be created in order to show the Loader  */}
        <ReportCard
          classname='col-4'
          icon={<i className={getAssetStatusIcon(assetsInfo[0]?.assetStatusDescription)} />}
          key={assetsInfo[0]?.assetStatusDescription}
          status={assetsInfo[0]?.assetStatusAmount?.toString()}
          title={`${assetsInfo[0]?.assetStatusDescription} Assets`}
          iconModifier={getModifierAsset(assetsInfo[0]?.assetStatusDescription)}
          loading={loading}
        />
        <ReportCard
          classname='col-4'
          icon={<i className={getAssetStatusIcon(assetsInfo[1]?.assetStatusDescription)} />}
          key={assetsInfo[1]?.assetStatusDescription}
          status={assetsInfo[1]?.assetStatusAmount?.toString()}
          title={`${assetsInfo[1]?.assetStatusDescription} Assets`}
          iconModifier={getModifierAsset(assetsInfo[1]?.assetStatusDescription)}
          loading={loading}
        />
        <ReportCard
          classname='col-4'
          icon={<i className={getAssetStatusIcon(assetsInfo[0]?.assetStatusDescription)} />}
          key={assetsInfo[2]?.assetStatusDescription}
          status={assetsInfo[2]?.assetStatusAmount?.toString()}
          title={`${assetsInfo[2]?.assetStatusDescription} Assets`}
          iconModifier={getModifierAsset(assetsInfo[2]?.assetStatusDescription)}
          loading={loading}
        />
        <ReportCard
          classname='col-4'
          icon={<i className={getAssetStatusIcon(assetsInfo[3]?.assetStatusDescription)} />}
          key={assetsInfo[3]?.assetStatusDescription}
          status={assetsInfo[3]?.assetStatusAmount?.toString()}
          title={`${assetsInfo[3]?.assetStatusDescription}`}
          iconModifier={getModifierAsset(assetsInfo[3]?.assetStatusDescription)}
          loading={loading}
        />
      </div>
    </>
  );
};
export default Home;
